import React from "react";

import CloseOutlined from "@mui/icons-material/CloseOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { formatPrice } from "utility/formatters";

export const PremiumDetailsDialog = ({ open, onClose, calculatePremiumsResponse, selectedInsurancePlanName }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // Get all farms that have the selected insurance plan
  const getFarmsForInsurancePlan = () => {
    if (!calculatePremiumsResponse?.budgetCountyPractices?.[0]?.producerFarmFieldCrops || !selectedInsurancePlanName) {
      return [];
    }

    const farms = [];
    const producerFarmFieldCrops = calculatePremiumsResponse.budgetCountyPractices[0].producerFarmFieldCrops;

    producerFarmFieldCrops.forEach((farm) => {
      const matchingInsurancePlan = farm.insurancePlans?.find(
        (plan) => plan.insurance_plan_name === selectedInsurancePlanName
      );

      if (matchingInsurancePlan) {
        farms.push({
          farmId: farm.id,
          farmName: farm.farm_name,
          fsaFarmNumber: farm.fsa_farm_number,
          fsaTractNumber: farm.fsa_tract_number,
          fsaFieldNumber: farm.fsa_field_number,
          basicUnitNumber: farm.basic_unit_number,
          optionalUnitNumber: farm.optional_unit_number,
          reportedAcreage: farm.reported_acreage,
          sharePercent: farm.share_percent,
          approvedYield: farm.approved_yield,
          producerPremiumPerAcre:
            matchingInsurancePlan.producer_premium_amount_per_acre ||
            (farm.reported_acreage > 0 ? matchingInsurancePlan.producer_premium_amount / farm.reported_acreage : 0),
          liabilityPerAcre:
            farm.reported_acreage > 0 ? matchingInsurancePlan.liability_amount / farm.reported_acreage : 0,
          producerPremiumTotal: matchingInsurancePlan.producer_premium_amount,
          liabilityTotal: matchingInsurancePlan.liability_amount,
          coverageLevel: matchingInsurancePlan.coverage_level_percent,
        });
      }
    });

    return farms;
  };

  const farms = getFarmsForInsurancePlan();

  const formatUnitNumber = (farm) =>
    [farm.basicUnitNumber, farm.optionalUnitNumber].filter(Boolean).join("-") || "";

  // Group by unit number: sum premium/coverage/acres, avg yield and share %
  // Rebase $/ac from aggregated totals after grouping (not avg of pre-group $/ac)
  const rows = Object.values(
    farms.reduce((acc, farm, index) => {
      const unit = formatUnitNumber(farm);
      const key = unit || `unassigned-${index}`;

      if (!acc[key]) {
        acc[key] = {
          id: key,
          unit,
          acres: 0,
          sharePercentSum: 0,
          approvedYieldSum: 0,
          count: 0,
          totalPremium: 0,
          totalCoverage: 0,
        };
      }

      const row = acc[key];
      row.acres += farm.reportedAcreage || 0;
      row.sharePercentSum += farm.sharePercent || 0;
      row.approvedYieldSum += farm.approvedYield || 0;
      row.count += 1;
      row.totalPremium += farm.producerPremiumTotal || 0;
      row.totalCoverage += farm.liabilityTotal || 0;
      return acc;
    }, {})
  )
    .map((row) => {
      const acres = row.acres;
      return {
        id: row.id,
        unit: row.unit,
        acres,
        sharePercent: row.count ? row.sharePercentSum / row.count : 0,
        approvedYield: row.count ? row.approvedYieldSum / row.count : 0,
        // Rebase per-acre rates from unit totals
        premiumPerAcre: acres > 0 ? row.totalPremium / acres : 0,
        coveragePerAcre: acres > 0 ? row.totalCoverage / acres : 0,
        totalPremium: row.totalPremium,
        totalCoverage: row.totalCoverage,
      };
    })
    .sort((a, b) => a.unit.localeCompare(b.unit, undefined, { numeric: true }));

  // Define columns for TilleyDataGrid
  const columns = [
    {
      field: "unit",
      headerName: "Unit",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "acres",
      headerName: "Acres",
      align: "right",
      headerAlign: "right",
      flex: 1,
      minWidth: 100,
      valueFormatter: (value) => value?.toLocaleString(),
    },
    {
      field: "sharePercent",
      headerName: "Share %",
      align: "right",
      headerAlign: "right",
      flex: 1,
      minWidth: 100,
      valueFormatter: (value) => `${(value * 100).toFixed(0)}%`,
    },
    {
      field: "approvedYield",
      headerName: "Approved Yield",
      align: "right",
      headerAlign: "right",
      flex: 1,
      minWidth: 130,
      valueFormatter: (value) => (value != null ? Number(value).toFixed(1) : ""),
    },
    {
      field: "premiumPerAcre",
      headerName: "Producer Premium $/ac",
      align: "right",
      headerAlign: "right",
      flex: 1,
      minWidth: 120,
      cellClassName: "grid-column-group-a first",
      valueFormatter: (value) => formatPrice(value, 2),
    },
    {
      field: "coveragePerAcre",
      headerName: "Coverage $/ac",
      align: "right",
      headerAlign: "right",
      flex: 1,
      minWidth: 120,
      cellClassName: "grid-column-group-a last",
      valueFormatter: (value) => formatPrice(value, 2),
    },
    {
      field: "totalPremium",
      headerName: "Producer Premium Total",
      align: "right",
      headerAlign: "right",
      flex: 1,
      minWidth: 120,
      valueFormatter: (value) => formatPrice(value, 0),
    },
    {
      field: "totalCoverage",
      headerName: "Total Coverage",
      align: "right",
      headerAlign: "right",
      flex: 1,
      minWidth: 120,
      valueFormatter: (value) => formatPrice(value, 0),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle variant="h6">Unit Detail - {selectedInsurancePlanName}</DialogTitle>
      <DialogContent>
        {rows.length > 0 ? (
          <>
            {/* Mobile Card Layout */}
            {isMobile ? (
              <Stack spacing={2}>
                {rows.map((row) => (
                  <Card key={row.id} variant="outlined">
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Unit
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {row.unit}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Acres
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {row.acres?.toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Share %
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {(row.sharePercent * 100).toFixed(0)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Approved Yield
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {row.approvedYield != null ? Number(row.approvedYield).toFixed(1) : ""}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="body2"
                            sx={{ color: "var(--mui-palette-text-primary)", fontWeight: "600" }}
                          >
                            Producer Premium $/ac
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold", color: "var(--mui-palette-text-primary)", fontSize: "1.1rem" }}
                          >
                            {formatPrice(row.premiumPerAcre, 2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="body2"
                            sx={{ color: "var(--mui-palette-text-primary)", fontWeight: "600" }}
                          >
                            Coverage $/ac
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: "bold", color: "#000", fontSize: "1.1rem" }}>
                            {formatPrice(row.coveragePerAcre, 2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: "#999", fontSize: "0.8rem" }}>
                            Producer Premium Total
                          </Typography>
                          <Typography variant="body1" sx={{ color: "#666", fontSize: "0.9rem" }}>
                            {formatPrice(row.totalPremium, 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: "#999", fontSize: "0.8rem" }}>
                            Total Coverage
                          </Typography>
                          <Typography variant="body1" sx={{ color: "#666", fontSize: "0.9rem" }}>
                            {formatPrice(row.totalCoverage, 0)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              /* Desktop DataGrid Layout */
              <TilleyDataGrid
                rows={rows}
                columns={columns}
                pageSizeOptions={[5, 10, 25]}
                defaultPageSize={isMobile ? 5 : 10}
                gridKey="premium-details-dialog"
                getRowId={(row) => row.id}
                hideFooterPagination={rows.length <= 5}
                hideFooterSelectedRowCount={true}
                sx={{
                  maxWidth: "100%",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f4f7ed",
                  },
                  "& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold",
                  },
                  "& .grid-column-group-a .MuiDataGrid-columnHeaderTitle": {
                    color: "var(--mui-palette-text-primary)",
                    fontSize: "0.95rem",
                    fontWeight: "bold",
                  },
                  "& .MuiDataGrid-cell.grid-column-group-a": {
                    fontWeight: "600",
                    color: "var(--mui-palette-text-primary)",
                    fontSize: "0.95rem",
                  },
                  "& .MuiDataGrid-columnHeader[data-field='totalPremium'] .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader[data-field='totalCoverage'] .MuiDataGrid-columnHeaderTitle":
                    {
                      color: "var(--mui-palette-text-secondary)",
                      fontSize: "0.85rem",
                    },
                  "& .MuiDataGrid-cell[data-field='totalPremium'], & .MuiDataGrid-cell[data-field='totalCoverage']": {
                    color: "var(--mui-palette-text-secondary)",
                    fontSize: "0.85rem",
                  },
                }}
              />
            )}
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No farm data available for this insurance plan.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onClose}>
          <CloseOutlined sx={{ mr: 1 }} />
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
